/**
 * OutputDeviceSelector (F3) — let the host route TTS playback to a chosen
 * speaker/headset via `HTMLMediaElement.setSinkId` (Chromium only).
 *
 * Enumerates `audiooutput` devices and emits the chosen deviceId upward via
 * `onChange`; the parent wires it into `TtsPlayer.setSinkId`. On browsers
 * without output-device selection (Safari/Firefox), the control renders a
 * short "not supported — system default output" notice instead of a dropdown.
 * Labels populate only after a capture permission has been granted, so this is
 * best placed alongside the input DeviceSelector (which requests it).
 */

import { useEffect, useState, type ReactElement } from 'react';
import {
  listAudioOutputDevices,
  type AudioOutputDevice,
} from '@/features/capture/audioDevices';

interface OutputDeviceSelectorProps {
  /** Disable interaction (e.g. while in contingency / subtitle-only mode). */
  disabled?: boolean;
  /** Whether the browser supports setSinkId (passed from useTtsPlayback). */
  supported: boolean;
  /** Fires with the chosen output deviceId ('' = system default). */
  onChange: (deviceId: string) => void;
}

export function OutputDeviceSelector({
  disabled = false,
  supported,
  onChange,
}: OutputDeviceSelectorProps): ReactElement {
  const [devices, setDevices] = useState<AudioOutputDevice[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const list = await listAudioOutputDevices();
        if (!cancelled) setDevices(list);
      } catch {
        if (!cancelled) setDevices([]);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  if (!supported) {
    return (
      <p style={styles.hint}>
        Seleção de dispositivo de saída não é suportada neste navegador
        (disponível no Chrome/Edge). O áudio usará a saída padrão do sistema.
      </p>
    );
  }

  return (
    <label style={styles.field}>
      <span style={styles.label}>Dispositivo de saída (áudio TTS)</span>
      <select
        value={deviceId}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          setDeviceId(next);
          onChange(next);
        }}
        style={styles.input}
      >
        <option value="">Saída padrão do sistema</option>
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  input: {
    padding: '0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.9rem',
  },
  hint: { margin: 0, fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
};
